import type { CycleRole } from '@/api/cyclesService';
import type { PlanType } from '@/api/plansService';
import type { CycleMeta, SequenceRow } from './planComposerTypes';

export interface CycleAllocation {
  uid: string;
  name: string;
  role: CycleRole;
  minWeeks: number;
  effectiveMaxWeeks: number;
  rankedWeeks: number;
  capWeeks: number | null;
}

export const effectiveMaxWeeks = (meta: CycleMeta, formatIdentifier: string, weekCount: number | null): number => {
  const ranked = meta.priorityByFormat[formatIdentifier] ?? 0;
  return Math.max(0, Math.min(meta.maxWeekNumber, weekCount ?? Infinity, ranked));
};

export const allocationOf = (rows: SequenceRow[], meta: Record<string, CycleMeta>, formatIdentifier: string): CycleAllocation[] =>
  rows.map((r) => {
    const m = meta[r.cycleId];
    if (!m) return { uid: r.uid, name: 'Cycle', role: 'NORMAL', minWeeks: 0, effectiveMaxWeeks: 0, rankedWeeks: 0, capWeeks: null };
    return {
      uid: r.uid,
      name: m.name,
      role: m.role,
      minWeeks: m.minWeekNumber,
      effectiveMaxWeeks: effectiveMaxWeeks(m, formatIdentifier, r.weekCount),
      rankedWeeks: m.priorityByFormat[formatIdentifier] ?? 0,
      capWeeks: r.weekCount,
    };
  });

/** R41: the recovery is appended after the race, so it is in none of the fitted durations. */
const prep = (alloc: CycleAllocation[]): CycleAllocation[] => alloc.filter((c) => c.role !== 'RECOVERY');

export const dminWeeks = (alloc: CycleAllocation[]): number => prep(alloc).reduce((s, c) => s + c.minWeeks, 0);
export const dmaxWeeks = (alloc: CycleAllocation[]): number => prep(alloc).reduce((s, c) => s + c.effectiveMaxWeeks, 0);
export const recoveryWeeks = (alloc: CycleAllocation[]): number =>
  alloc.filter((c) => c.role === 'RECOVERY').reduce((s, c) => s + c.effectiveMaxWeeks, 0);

/** Weeks kept per cycle in the shortest condensed preparation (min version cut from the start). */
export const condensedWeeks = (alloc: CycleAllocation[], thresholdWeeks: number): number[] => {
  let toRemove = dminWeeks(alloc) - thresholdWeeks;
  return alloc.map((c) => {
    if (c.role === 'RECOVERY') return c.effectiveMaxWeeks;
    const removed = Math.min(c.minWeeks, Math.max(0, toRemove));
    toRemove -= removed;
    return c.minWeeks - removed;
  });
};

export const PLAN_TYPE_LABELS: Record<string, string> = {
  spe: 'Spécifique',
  reprise: 'Reprise',
  generic: 'Généraliste',
};
export const DAY_MODE_LABELS: Record<string, string> = { ATHLETE: "Choisis par l'athlète", FIXED: 'Fixes' };
/** D-b: the technical format a Débutant plan (no format) is ranked under. */
export const BEGINNER_FORMAT = 'BEGINNER';

/** D-a: the plan type is no longer authored, it follows the race type. */
export const planTypeOfRaceType = (identifier: string | undefined): PlanType =>
  identifier === 'GEN' ? 'generic' : identifier === 'BEGINNER' ? 'reprise' : 'spe';

export const FORMAT_SUBTITLES: Record<string, string> = {
  XS: 'Très courte distance',
  S: 'Courte distance',
  M: 'Distance olympique',
  L: 'Longue distance',
  XL: 'Très longue distance',
  '5KM': 'Course à pied',
  '10KM': 'Course à pied',
  HALF_MARATHON: 'Course à pied',
  MARATHON: 'Course à pied',
};
